import classNames from 'classnames';
import React, {
    ReactElement,
    ReactNode,
    Children,
    useState,
    SetStateAction,
    Dispatch,
    useEffect,
    useRef,
    MutableRefObject,
} from 'react';

import { tabsDisplayName } from './TabNavigation.qa';

type CustomReactElement = ReactElement | ReactElement[];

export interface ITabProps {
    children: CustomReactElement;
    className?: string;
    defaultActiveIndex?: number;
    currentActiveIndex?: number;
    onTabChange?: (currentTabIndex: number) => void;
    'data-testid'?: string;
}

const isLinkIndexValid = (
    children: CustomReactElement,
    activeIndex: number,
): boolean => {
    const recomposeChildren: CustomReactElement = Children
        .map(children, (child): ReactElement => {
            return child;
        });

    // since activeIndex starts from 0, reduce the array length by 1
    if (activeIndex > recomposeChildren.length - 1) {
        return false;
    }

    return true;
};

const getActiveIndex = (
    children: CustomReactElement,
    defaultActiveKey: number,
    activeKey: number,
): number => {
    const FIRST_LINK_INDEX: number = 0;

    if (!children) {
        return activeKey;
    }

    if (!activeKey && defaultActiveKey !== 0) {
        activeKey = defaultActiveKey;
    }

    const isActiveIndexValid: boolean = isLinkIndexValid(children, activeKey);

    if (!isActiveIndexValid) {
        return FIRST_LINK_INDEX;
    }

    return activeKey;
};

export function OzTabNavigation (props: ITabProps): ReactElement | null {
    const {
        children,
        className = '',
        'data-testid': dataTestID = '',
        defaultActiveIndex = 0,
        currentActiveIndex = defaultActiveIndex || 0,
        onTabChange,
    }: ITabProps = props;

    const INITIAL_ACTIVE_INDEX: number = 0;

    const initialActiveIndex: number = getActiveIndex(
        children,
        currentActiveIndex,
        INITIAL_ACTIVE_INDEX,
    );

    const [activeIndex, setActiveIndex]:
    [number, Dispatch<SetStateAction<number>>] = useState<number>(initialActiveIndex);

    const isInitialMount: MutableRefObject<boolean> = useRef(true);

    useEffect((): void => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setActiveIndex(initialActiveIndex);

            if (onTabChange) {
                onTabChange(initialActiveIndex);
            }
        }
    }, [initialActiveIndex, onTabChange]);

    if (!children) {
        return null;
    }

    function navigateToTab (currentIndex: number): void {
        setActiveIndex(currentIndex);

        if (onTabChange) {
            onTabChange(currentIndex);
        }
    }

    const renderTabsHeader = (): ReactNode => {
        return Children.map(children, (child, index): ReactElement => {
            const currentLinkIndex: number = index;
            const isNavLinkActive: boolean = currentLinkIndex === activeIndex;

            const tabHeaderClassNames: string = classNames({
                'mr-2 hover:text-primary hover:opacity-100 hover:cursor-pointer focus:font-bold focus:text-black': true,
                'opacity-75 text-black': !isNavLinkActive,
                'text-primary border-b-2 border-primary': isNavLinkActive,
                [className]: !!className,
            });

            return (
                <div
                    className={tabHeaderClassNames}
                    key={currentLinkIndex}
                    onClick={(): void => {
                        navigateToTab(currentLinkIndex);
                    }}
                >
                    {child}
                </div>
            );
        });
    };

    return (
        <div
            className='flex flex-row py-3 font-primary font-bold type-overline type-button'
            data-testid={dataTestID}
        >
            {renderTabsHeader()}
        </div>
    );
}

OzTabNavigation.displayName = tabsDisplayName;
